import React, { useState } from 'react'
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Button,
  IconButton
} from '@mui/material'
import {
  HomeOutlined as HeroIcon,
  ViewCarouselOutlined as CarouselIcon,
  CategoryOutlined as ProductsIcon,
  ViewModuleOutlined as BannersIcon,
  ViewModuleOutlined,
  Add as AddIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  DragIndicator as DragIcon
} from '@mui/icons-material'
import { useStyles } from './style'
import AddSectionModal from '../add-section-modal'

const SectionsSelector = ({ sections, selectedSection, onSectionSelect, onAddSection, onReorderSections, isEdit }) => {
  const classes = useStyles()
  const [modalOpen, setModalOpen] = useState(false)

  const [draggedIndex, setDraggedIndex] = useState(null)
  const [dragOverIndex, setDragOverIndex] = useState(null)
  const [isDragging, setIsDragging] = useState(false)

  const availableSectionTypes = [
    {
      type: 'mtBanner',
      label: 'Hero Section',
      icon: <HeroIcon />,
      description: 'Main banner with title, subtitle, description, CTA and image'
    },
    {
      type: 'featuresBanner',
      label: 'Featured Products',
      icon: <ProductsIcon />,
      description: 'Grid of product cards with titles, descriptions and links'
    },
    {
      type: 'contentExplorer',
      label: 'Explore Content Carousel',
      icon: <CarouselIcon />,
      description: 'Content carousel section with customizable title'
    },
    {
      type: 'featureCard',
      label: 'Product Banners',
      icon: <BannersIcon />,
      description: 'Promotional banners with images, titles, descriptions and CTAs'
    }
  ]

  const handleAddSection = (sectionType) => {
    onAddSection(sectionType)
    setModalOpen(false)
  }

  const handleDragStart = (e, index) => {
    setDraggedIndex(index)
    setIsDragging(true)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/html', e.target.outerHTML)
  }

  const handleDragOver = (e, index) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'

    // Update drag over index to show where item will be dropped
    if (index !== draggedIndex && draggedIndex !== null) {
      setDragOverIndex(index)
    }
  }

  const handleDragEnter = (e, index) => {
    e.preventDefault()
    if (index !== draggedIndex && draggedIndex !== null) {
      setDragOverIndex(index)
    }
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    // Only clear dragOverIndex if we're leaving the entire list area
    const rect = e.currentTarget.getBoundingClientRect()
    const isLeavingList = (
      e.clientY < rect.top ||
      e.clientY > rect.bottom ||
      e.clientX < rect.left ||
      e.clientX > rect.right
    )

    if (isLeavingList) {
      setDragOverIndex(null)
    }
  }

  const handleDrop = (e, dropIndex) => {
    e.preventDefault()
    if (draggedIndex !== null && draggedIndex !== dropIndex) {
      onReorderSections(draggedIndex, dropIndex)
    }
    setDraggedIndex(null)
    setDragOverIndex(null)
    setIsDragging(false)
  }

  const handleDragEnd = () => {
    setDraggedIndex(null)
    setDragOverIndex(null)
    setIsDragging(false)
  }

  const handleMoveSection = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1
    if (newIndex >= 0 && newIndex < sections.length) {
      onReorderSections(index, newIndex)
    }
  }

  const getSectionIcon = (type) => {
    const sectionType = availableSectionTypes.find(s => s.type === type)
    return sectionType?.icon || <ViewModuleOutlined />
  }

  const getSectionLabel = (section) => {
    const sectionType = availableSectionTypes.find(s => s.type === section.type)
    return `${sectionType?.label || section.type} ${section.instanceId ? `#${section.instanceId}` : ''}`
  }

  const getSectionDescription = (section) => {
    const sectionType = availableSectionTypes.find(s => s.type === section.type)
    return sectionType?.description || ''
  }

  return (
    <Box className={classes.container}>
      {/* Header Section */}
      <Box className={classes.header}>
        <Typography variant='h6' className={classes.title}>
          Sections
        </Typography>
        <Typography variant='body2' color='textSecondary' className={classes.subtitle}>
          Configure the sections that will appear on your homepage
        </Typography>
      </Box>

      <Divider />

      {/* Sections List */}
      <Box className={classes.sectionsList}>
        <List>
          {sections.map((section, index) => {
            const isBeingDragged = draggedIndex === index
            const isDragTarget = dragOverIndex === index
            const shouldShowDropZone = isDragTarget && draggedIndex !== null && draggedIndex !== index

            return (
              <React.Fragment key={`${section.type}-${section.instanceId || index}`}>
                {/* Drop Zone Above Target Item */}
                {shouldShowDropZone && draggedIndex > index && (
                  <Box className={classes.dropZone}>
                    <Box className={classes.dropIndicator} />
                  </Box>
                )}

                {/* Section List Item */}
                <ListItem
                  disablePadding
                  draggable={isEdit}
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnter={(e) => handleDragEnter(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`
                    ${isBeingDragged ? classes.draggedItem : ''}
                    ${isDragTarget ? classes.dragTarget : ''}
                    ${isDragging ? classes.isDragging : ''}
                  `.trim()}
                >
                  <ListItemButton
                    selected={selectedSection?.order === section.order}
                    onClick={() => onSectionSelect(section)}
                    className={classes.sectionItem}
                  >
                    {/* Section Icon */}
                    <ListItemIcon className={classes.sectionIcon}>
                      {getSectionIcon(section.type)}
                    </ListItemIcon>

                    {/* Section Text */}
                    <ListItemText
                      primary={getSectionLabel(section)}
                      secondary={getSectionDescription(section)}
                      primaryTypographyProps={{
                        fontWeight: selectedSection?.order === section.order ? 600 : 400
                      }}
                    />

                    {/* Arrow Buttons for Reordering - Only in edit mode */}
                    {isEdit && (
                      <Box className={classes.arrowContainer}>
                        <IconButton
                          size='small'
                          onClick={(e) => {
                            e.stopPropagation()
                            handleMoveSection(index, 'up')
                          }}
                          disabled={index === 0}
                          className={classes.arrowButton}
                        >
                          <ArrowUpIcon className={classes.arrowIcon} />
                        </IconButton>
                        <IconButton
                          size='small'
                          onClick={(e) => {
                            e.stopPropagation()
                            handleMoveSection(index, 'down')
                          }}
                          disabled={index === sections.length - 1}
                          className={classes.arrowButton}
                        >
                          <ArrowDownIcon className={classes.arrowIcon} />
                        </IconButton>
                      </Box>
                    )}

                    {/* Drag Handle - Only in edit mode */}
                    {isEdit && (
                      <Box className={classes.dragIconContainer}>
                        <DragIcon className={classes.dragHoverIcon} />
                      </Box>
                    )}
                  </ListItemButton>
                </ListItem>

                {/* Drop Zone Below Target Item */}
                {shouldShowDropZone && draggedIndex < index && (
                  <Box className={classes.dropZone}>
                    <Box className={classes.dropIndicator} />
                  </Box>
                )}
              </React.Fragment>
            )
          })}
        </List>
      </Box>

      {isEdit && (
        <>
          <Divider />
          <Box className={classes.addSection} data-test='sections-selector-add-section-container'>
            <Button
              variant='outlined'
              startIcon={<AddIcon />}
              onClick={() => setModalOpen(true)}
              fullWidth
              className={classes.addButton}
              data-test='sections-selector-add-section-button'
            >
              Add Section
            </Button>
          </Box>
        </>
      )}

      {/* Add Section Modal */}
      <AddSectionModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onAddSection={handleAddSection}
      />
    </Box>
  )
}

export default SectionsSelector
